import { useState, type ChangeEvent } from 'react';
import { RotateCcw } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  CONTENTIOUS,
  contentiousTotals,
  formatGBPCompact,
} from '@/lib/roi/data';

interface ContentiousState {
  annualSpend: number;
  writeOffPercent: number;
  reductionPercent: number;
}

const SPEND_LIMIT = { min: 100_000, max: 15_000_000, step: 100_000 };
const PERCENT_LIMIT = { min: 0, max: 90, step: 5 };

function defaultState(): ContentiousState {
  return {
    annualSpend: CONTENTIOUS.annualSpend,
    writeOffPercent: CONTENTIOUS.writeOffPercent,
    reductionPercent: CONTENTIOUS.reductionPercent,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function ContentiousModule() {
  const [state, setState] = useState<ContentiousState>(defaultState);
  const model = { ...CONTENTIOUS, ...state };
  const totals = contentiousTotals(model);

  const updateField =
    (
      key: keyof ContentiousState,
      limits: { min: number; max: number; step: number },
    ) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const next = Number(event.target.value);
      if (Number.isNaN(next)) return;
      setState((current) => ({
        ...current,
        [key]: clamp(next, limits.min, limits.max),
      }));
    };

  return (
    <motion.section
      aria-label="Contentious insolvency ROI"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
      className="pitch-card relative overflow-hidden rounded-2xl p-6 sm:p-10"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute right-[-20%] top-[-30%] z-0"
        style={{
          width: '720px',
          height: '420px',
          borderRadius: '720px',
          opacity: 0.16,
          background: '#3B82F6',
          filter: 'blur(110px)',
        }}
      />

      <div className="relative z-10 flex flex-col gap-10">
        <header className="flex flex-col gap-3">
          <span className="text-xs font-medium uppercase tracking-[0.12em] text-ink-dim">
            Contentious insolvency
          </span>
          <h2 className="max-w-2xl text-2xl font-semibold leading-tight text-ink-strong sm:text-3xl">
            Avoiding the cases that won’t pay
          </h2>
          <p className="max-w-2xl text-sm leading-relaxed text-ink-muted">
            Contentious recovery work can absorb millions of pounds of annual
            WIP, with a large share written off when contingent cases do not
            realise value. Asset Hound’s inception risk analysis helps firms
            avoid a meaningful share of that unpaid work
          </p>
        </header>

        <div className="grid gap-6 md:grid-cols-3">
          <Figure
            label="Annual contentious spend"
            value={formatGBPCompact(state.annualSpend)}
          />
          <Figure
            label="Write-off at risk"
            value={formatGBPCompact(totals.writeOff)}
            note={`${state.writeOffPercent}% of annual contentious spend`}
          />
          <Figure
            label="Annual saving"
            value={formatGBPCompact(totals.saving)}
            note={`Residual write-off ${formatGBPCompact(totals.residualWriteOff)}`}
            highlight
          />
        </div>

        <div className="grid gap-5 border-t border-border-subtle pt-8 md:grid-cols-[1fr_1fr_1fr_auto] md:items-end">
          <ContentiousControl
            id="contentious-annual-spend"
            label="Annual contentious spend"
            value={state.annualSpend}
            limits={SPEND_LIMIT}
            prefix="£"
            onChange={updateField('annualSpend', SPEND_LIMIT)}
          />
          <ContentiousControl
            id="contentious-write-off-rate"
            label="Write-off rate"
            value={state.writeOffPercent}
            limits={PERCENT_LIMIT}
            suffix="%"
            onChange={updateField('writeOffPercent', PERCENT_LIMIT)}
          />
          <ContentiousControl
            id="contentious-reduction-rate"
            label="Risk analysis reduction"
            value={state.reductionPercent}
            limits={PERCENT_LIMIT}
            suffix="%"
            onChange={updateField('reductionPercent', PERCENT_LIMIT)}
          />
          <button
            type="button"
            onClick={() => setState(defaultState())}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-accent-blue-border px-4 text-sm font-medium text-brand-500 transition hover:border-brand-500 hover:bg-accent-blue-bg focus:outline-none focus:ring-2 focus:ring-brand-500/40 active:scale-[0.98]"
          >
            <RotateCcw aria-hidden className="h-4 w-4" />
            Reset
          </button>
        </div>

        <div className="grid gap-8 border-t border-border-subtle pt-8 sm:grid-cols-2">
          <div className="flex flex-col gap-3">
            <span className="text-[13px] font-medium text-ink-muted">
              Why this is shown differently
            </span>
            <p className="text-sm leading-relaxed text-ink">
              Saving here is from not taking on cases that won’t pay. The
              calculator is measuring avoided unpaid work, rather than
              charge-out hours saved on standard cases
            </p>
            <dl className="mt-2 flex flex-col gap-2 text-sm">
              <Row label="Fee model" value="% of realisation" />
              <Row label="Success rate" value={CONTENTIOUS.successRate} />
              <Row
                label="WIP at risk per case"
                value={CONTENTIOUS.wipAtRiskPerCase}
              />
            </dl>
          </div>

          <div className="flex flex-col gap-3">
            <span className="text-[13px] font-medium text-ink-muted">
              Platform capabilities
            </span>
            <ul className="flex flex-col gap-2 text-sm leading-relaxed text-ink">
              {CONTENTIOUS.capabilities.map((cap) => (
                <li key={cap} className="flex gap-2">
                  <span
                    aria-hidden
                    className="mt-[7px] block h-1 w-1 flex-none rounded-full bg-brand-500"
                  />
                  <span>{cap}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

function Figure({
  label,
  value,
  note,
  highlight = false,
}: {
  label: string;
  value: string;
  note?: string;
  highlight?: boolean;
}) {
  return (
    <div className="pitch-card flex flex-col gap-2 rounded-xl p-5">
      <span className="text-xs text-ink-dim">{label}</span>
      <span
        className={
          'font-semibold tabular-nums [line-height:1] ' +
          (highlight
            ? 'text-brand-500 [font-size:40px]'
            : 'text-ink-strong [font-size:32px]')
        }
      >
        {value}
      </span>
      {note ? <span className="text-xs text-ink-muted">{note}</span> : null}
    </div>
  );
}

function ContentiousControl({
  id,
  label,
  value,
  limits,
  onChange,
  prefix,
  suffix,
}: {
  id: string;
  label: string;
  value: number;
  limits: { min: number; max: number; step: number };
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  prefix?: string;
  suffix?: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <label htmlFor={id} className="text-[13px] font-medium text-ink-muted">
        {label}
      </label>
      <div className="flex items-center rounded-md border border-border-strong bg-bg-input px-3 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/30">
        {prefix ? (
          <span className="text-sm text-ink-muted">{prefix}</span>
        ) : null}
        <input
          id={id}
          type="number"
          min={limits.min}
          max={limits.max}
          step={limits.step}
          value={value}
          onChange={onChange}
          className="h-11 min-w-0 flex-1 bg-transparent px-2 text-sm font-medium tabular-nums text-ink-strong outline-none"
        />
        {suffix ? (
          <span className="text-sm text-ink-muted">{suffix}</span>
        ) : null}
      </div>
      <input
        aria-label={`${label} slider`}
        type="range"
        min={limits.min}
        max={limits.max}
        step={limits.step}
        value={value}
        onChange={onChange}
        className="h-2 w-full cursor-pointer accent-brand-500"
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border-divider pb-2 last:border-0 last:pb-0">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-ink-strong">{value}</dd>
    </div>
  );
}
